import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AlertTriangle, Mailbox, X, FileText, Rocket } from "lucide-react";

export default function WorkspaceBanners() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [health, setHealth] = useState(null);
  const [onb, setOnb] = useState(null);
  const [ent, setEnt] = useState(null);
  const [prop, setProp] = useState(null);
  const [wiz, setWiz] = useState(null);
  const [dismissHealth, setDismissHealth] = useState(false);
  const [dismissOnb, setDismissOnb] = useState(() => localStorage.getItem("vis_onboarding_dismissed") === "1");

  const load = () => {
    api.get("/mailbox/health").then((r) => setHealth(r.data)).catch(() => {});
    api.get("/tenant/entitlements").then((r) => setEnt(r.data)).catch(() => {});
    if (user.role === "admin") {
      api.get("/tenant/onboarding").then((r) => setOnb(r.data)).catch(() => {});
      api.get("/proposals/setup").then((r) => setProp(r.data)).catch(() => {});
      api.get("/onboarding/wizard").then((r) => setWiz(r.data)).catch(() => {});
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showHealth = health?.needs_reconnect && !dismissHealth;
  const showOnb = user.role === "admin" && onb && !onb.operational && !dismissOnb;
  const showProp = user.role === "admin" && prop && !prop.ready;
  const showUpgrade = ent?.upgrade_required;
  const showWiz = user.role === "admin" && wiz && !wiz.completed;
  if (!showHealth && !showOnb && !showProp && !showUpgrade && !showWiz) return null;

  return (
    <div className="px-8 pt-4 space-y-2">
      {showWiz && (
        <div data-testid="wizard-banner"
          className="flex items-center justify-between gap-3 border border-[#1C2530]/25 bg-[#1C2530]/5 px-4 py-2.5 rounded-sm">
          <div className="flex items-center gap-2 text-sm text-[#1C2530]">
            <Rocket className="w-4 h-4 shrink-0 text-[#3E8F99]" />
            <span>Finish setting up your workspace — {wiz.steps?.length || 5} quick steps to get your team live.</span>
          </div>
          <button data-testid="wizard-resume-btn" onClick={() => nav("/onboarding")}
            className="font-mono text-[10px] uppercase tracking-wider text-[#3E8F99] hover:underline shrink-0">Resume setup</button>
        </div>
      )}
      {showUpgrade && (
        <div data-testid="upgrade-banner"
          className="flex items-center justify-between gap-3 border border-[#5B3DF5]/40 bg-[#5B3DF5]/10 px-4 py-2.5 rounded-sm">
          <div className="flex items-center gap-2 text-sm text-[#3b269c]">
            <AlertTriangle className="w-4 h-4 shrink-0 text-[#5B3DF5]" />
            <span>
              {ent.trial_expired ? "Your trial has ended." : "You've reached your trial usage cap."}
              {" "}Contact the platform team to upgrade and keep using AI features.
            </span>
          </div>
        </div>
      )}
      {showHealth && (
        <div data-testid="connection-health-banner"
          className="flex items-center justify-between gap-3 border border-[#B23A2E]/40 bg-[#B23A2E]/10 px-4 py-2.5 rounded-sm">
          <div className="flex items-center gap-2 text-sm text-[#7d271d]">
            <AlertTriangle className="w-4 h-4 shrink-0 text-[#B23A2E]" />
            <span>
              {health.reconnect.map((r) => (r.provider === "outlook" ? "Outlook" : "Gmail")).join(" & ")} mailbox needs reconnection.
              {user.role === "admin"
                ? " Reconnect to keep outreach sending from your own address."
                : " Ask your workspace admin to reconnect it."}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {user.role === "admin" && (
              <button data-testid="mailbox-reconnect-link" onClick={() => nav("/settings")}
                className="font-mono text-[10px] uppercase tracking-wider text-[#B23A2E] hover:underline">Reconnect in Settings</button>
            )}
            <button data-testid="health-dismiss-btn" onClick={() => setDismissHealth(true)}
              className="text-[#B23A2E]/60 hover:text-[#B23A2E]"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}
      {showOnb && (
        <div data-testid="onboarding-banner"
          className="flex items-center justify-between gap-3 border border-[#E2711D]/40 bg-[#E2711D]/10 px-4 py-2.5 rounded-sm">
          <div className="flex items-center gap-2 text-sm text-[#8a4a12]">
            <Mailbox className="w-4 h-4 shrink-0 text-[#E2711D]" />
            <span>Finish setup — connect a Gmail or Outlook mailbox to make this workspace operational.</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="onboarding-connect-btn" onClick={() => nav("/settings")}
              className="font-mono text-[10px] uppercase tracking-wider text-[#E2711D] hover:underline">Connect now</button>
            <button data-testid="onboarding-dismiss-btn"
              onClick={() => { setDismissOnb(true); localStorage.setItem("vis_onboarding_dismissed", "1"); }}
              className="text-[#E2711D]/60 hover:text-[#E2711D]"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}
      {showProp && (
        <div data-testid="proposal-setup-card"
          className="flex items-center justify-between gap-3 border border-[#3E8F99]/40 bg-[#3E8F99]/10 px-4 py-2.5 rounded-sm">
          <div className="flex items-center gap-2 text-sm text-[#276169]">
            <FileText className="w-4 h-4 shrink-0 text-[#3E8F99]" />
            <span>
              Set up Proposal Intelligence — still needed:{" "}
              {[!prop.has_active_offering && "create an active offering", !prop.threshold_set && "set a discount threshold"]
                .filter(Boolean).join(" and ")}
              {" "}to enable proposals.
            </span>
          </div>
          <button data-testid="proposal-setup-btn" onClick={() => nav("/settings")}
            className="font-mono text-[10px] uppercase tracking-wider text-[#3E8F99] hover:underline shrink-0">Set up now</button>
        </div>
      )}
    </div>
  );
}
